import axios from "axios";
import { getBackendUrl } from "../config";

let apiRef = null;
let refreshPromise = null;
const authLostListeners = new Set();

export function bindApiInstance(api) {
	apiRef = api;
}

export function onAuthLost(listener) {
	authLostListeners.add(listener);
	return () => authLostListeners.delete(listener);
}

export function notifyAuthLost() {
	authLostListeners.forEach((fn) => {
		try {
			fn();
		} catch {
			/* ignore */
		}
	});
}

export function getStoredToken() {
	const raw = localStorage.getItem("token");
	if (!raw) return null;
	try {
		const parsed = JSON.parse(raw);
		if (typeof parsed === "string" && parsed.trim()) return parsed.trim();
	} catch {
		const t = String(raw).replace(/^"|"$/g, "").trim();
		if (t) return t;
	}
	return null;
}

export function setStoredToken(token) {
	if (!token) {
		localStorage.removeItem("token");
		if (apiRef?.defaults?.headers) {
			apiRef.defaults.headers.Authorization = undefined;
		}
		return;
	}
	localStorage.setItem("token", JSON.stringify(token));
	if (apiRef?.defaults?.headers) {
		apiRef.defaults.headers.Authorization = `Bearer ${token}`;
	}
}

export function clearStoredToken() {
	setStoredToken(null);
	notifyAuthLost();
}

export function decodeJwtPayload(token) {
	if (!token) return null;
	const parts = token.split(".");
	if (parts.length < 2) return null;
	try {
		const json = atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"));
		return JSON.parse(json);
	} catch {
		return null;
	}
}

/** true se o access token ainda está dentro do prazo (com folga de 30s). */
export function isAccessTokenAlive(token = getStoredToken()) {
	const payload = decodeJwtPayload(token);
	if (!payload?.exp) return Boolean(token);
	const now = Math.floor(Date.now() / 1000);
	return payload.exp > now + 30;
}

/**
 * Renova o access token via cookie httpOnly `jrt` no Node.
 * Deduplica chamadas concorrentes.
 */
export async function refreshAccessToken() {
	if (refreshPromise) {
		return refreshPromise;
	}

	refreshPromise = (async () => {
		const { data } = await axios.post(
			`${getBackendUrl()}/auth/refresh_token`,
			null,
			{ withCredentials: true }
		);
		if (!data?.token) {
			throw new Error("ERR_SESSION_EXPIRED");
		}
		setStoredToken(data.token);
		return data;
	})().finally(() => {
		refreshPromise = null;
	});

	return refreshPromise;
}

function errorMessage(error) {
	return String(error?.response?.data?.error || "");
}

/**
 * Só tenta refresh quando o backend indica token JWT inválido/expirado.
 * NÃO trata 403 de permissão (ERR_NO_PERMISSION, etc.) — isso deslogava o usuário.
 */
export function shouldAttemptTokenRefresh(error) {
	const status = error?.response?.status;
	const msg = errorMessage(error);

	if (msg === "ERR_COMPANY_INACTIVE" || msg === "ERR_USER_INACTIVE") {
		return false;
	}

	// Node isAuth / Go IsAuth: JWT inválido ou expirado
	if (status === 403 && /invalid token/i.test(msg)) {
		return true;
	}

	// Sem Bearer / sessão expirada (Go e Node)
	if (status === 401 && msg === "ERR_SESSION_EXPIRED") {
		return true;
	}

	return false;
}

export function shouldClearSession(error, { afterRefreshRetry = false } = {}) {
	const status = error?.response?.status;
	const msg = errorMessage(error);

	if (msg === "ERR_COMPANY_INACTIVE" || msg === "ERR_USER_INACTIVE") {
		return true;
	}

	// Depois de um refresh OK, não desloga por ERR_SESSION_EXPIRED residual
	// (retry sem header, race, etc.) — evita: refresh 200 → login.
	if (afterRefreshRetry) {
		return false;
	}

	if (status === 401 && msg === "ERR_SESSION_EXPIRED") {
		return true;
	}

	return false;
}
